import React, { useMemo } from 'react';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import {
  configApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import useAsync from 'react-use/esm/useAsync';
import { ScaffolderField } from '@backstage/plugin-scaffolder-react/alpha';
import {
  CircularProgress,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';

interface AdoProject {
  id: string;
  name: string;
  description?: string;
}

export const ADOProjectPicker = ({
  onChange,
  errors,
  rawErrors,
  required,
  help,
  disabled,
  schema: { title = 'ADO Project', description },
  formData,
  uiSchema: _uiSchema,
  formContext,
}: FieldExtensionComponentProps<string>) => {
  const config = useApi(configApiRef);
  const fetchApi = useApi(fetchApiRef);

  // Get organization from formContext (the full form data)
  const organization = formContext?.formData?.ADOOrganization || '';

  const backendUrl = config.getString('backend.baseUrl');
  const apiUrl = `${backendUrl}/api/ado-projects-lister/projects`;

  const { value, loading, error } = useAsync(async (): Promise<AdoProject[]> => {
    try {
      if (!organization) {
        return [];
      }

      console.log(
        `ADOProjectPicker: Fetching projects for org: ${organization}`,
      );

      const params = new URLSearchParams({
        organization,
      });
      const urlWithParams = `${apiUrl}?${params.toString()}`;

      const res = await fetchApi.fetch(urlWithParams, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
      });

      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(`HTTP ${res.status}: ${errorText}`);
      }

      const data: AdoProject[] = await res.json();
      console.log('ADOProjectPicker: Fetched projects:', data);

      return data;
    } catch (err) {
      console.error('ADOProjectPicker: Error fetching projects:', err);
      throw err;
    }
  }, [organization]);

  const projectOptions = useMemo(() => {
    if (!value) return [];
    return value.map(project => ({
      label: project.name,
      value: project.name,
      description: project.description,
    }));
  }, [value]);

  const handleChange = (event: any) => {
    onChange(event.target.value);
  };

  if (!organization) {
    return (
      <ScaffolderField
        rawErrors={rawErrors}
        errors={errors}
        required={required}
        help={help}
        rawDescription={description}
        disabled={disabled}
      >
        <FormControl fullWidth>
          <InputLabel>{title}</InputLabel>
          <Select disabled defaultValue="">
            <MenuItem value="">
              <em>Select a project</em>
            </MenuItem>
          </Select>
        </FormControl>
      </ScaffolderField>
    );
  }

  // Show loading state
  if (loading) {
    return (
      <ScaffolderField
        rawErrors={rawErrors}
        errors={errors}
        required={required}
        help={help}
        rawDescription={description}
        disabled={disabled}
      >
        <FormControl fullWidth>
          <InputLabel>{title}</InputLabel>
          <Select disabled defaultValue="">
            <MenuItem disabled>
              <CircularProgress size={20} style={{ marginRight: 10 }} />
              Loading projects...
            </MenuItem>
          </Select>
        </FormControl>
      </ScaffolderField>
    );
  }

  // Show error state
  if (error) {
    console.error('ADOProjectPicker: Error state:', error);
    return (
      <ScaffolderField
        rawErrors={[
          ...(rawErrors || []),
          `Failed to load projects: ${error.message}`,
        ]}
        errors={errors}
        required={required}
        help={help}
        rawDescription={description}
        disabled={disabled}
      >
        <Alert severity="error">
          Failed to load ADO projects: {error.message}
        </Alert>
      </ScaffolderField>
    );
  }

  // Show empty state
  if (projectOptions.length === 0) {
    return (
      <ScaffolderField
        rawErrors={required ? ['No projects available'] : rawErrors}
        errors={errors}
        required={required}
        help={help}
        rawDescription={description}
        disabled={disabled}
      >
        <Alert severity="warning">
          No ADO projects found in organization <strong>{organization}</strong> where you are project administrator
        </Alert>
      </ScaffolderField>
    );
  }

  // Show dropdown
  return (
    <ScaffolderField
      rawErrors={rawErrors}
      errors={errors}
      required={required}
      help={help}
      rawDescription={description}
      disabled={disabled}
    >
      <FormControl fullWidth required={required} error={rawErrors && rawErrors.length > 0}>
        <InputLabel>{title}</InputLabel>
        <Select
          value={formData || ''}
          onChange={handleChange}
          disabled={disabled || projectOptions.length === 0}
        >
          <MenuItem value="">
            <em>Select a project</em>
          </MenuItem>
          {projectOptions.map(option => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
              {option.description && ` - ${option.description}`}
            </MenuItem>
          ))}
        </Select>
        {help && <FormHelperText>{help}</FormHelperText>}
      </FormControl>
    </ScaffolderField>
  );
};
